import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Users, Building, Shield } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleDemoLogin = async (role) => {
    setLoading(true);
    try {
      const user = await login(role);
      toast.success(`Logged in as ${user.name}`);
      
      if (role === 'apprentice') {
        navigate('/apprentice/dashboard');
      } else if (role === 'employer') {
        navigate('/employer/dashboard');
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 pt-20">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-semibold text-[#1d1d1f] mb-3 sm:mb-4 leading-[1.05]">
            Welcome to <br className="sm:hidden" />ApprenticeshipMatch
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-[#86868b] px-4">Choose your demo account to explore the platform</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
          {/* Apprentice */}
          <button
            onClick={() => handleDemoLogin('apprentice')}
            className="bg-white border border-[#d2d2d7] rounded-3xl p-8 hover:shadow-xl transition-all duration-300 text-left group"
            data-testid="demo-login-apprentice"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6">
              <Users className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-3xl font-semibold text-[#1d1d1f] mb-2">Apprentice</h3>
            <p className="text-base text-[#86868b] mb-3">John Murphy</p>
            <p className="text-sm text-[#86868b]">Explore assessments, CV builder, and job matches</p>
          </button>

          {/* Employer */}
          <button
            onClick={() => handleDemoLogin('employer')}
            className="bg-white border border-[#d2d2d7] rounded-3xl p-8 hover:shadow-xl transition-all duration-300 text-left group"
            data-testid="demo-login-employer"
          >
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6">
              <Building className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-3xl font-semibold text-[#1d1d1f] mb-2">Employer</h3>
            <p className="text-base text-[#86868b] mb-3">Shane Glackin, STS Group</p>
            <p className="text-sm text-[#86868b]">Post roles, view candidates, manage pipeline</p>
          </button>
        </div>

        <div className="bg-[#f5f5f7] rounded-3xl p-8 max-w-2xl mx-auto">
          <h3 className="text-2xl font-semibold text-[#1d1d1f] mb-6">Or sign in with email</h3>
          <div className="space-y-4">
            <div>
              <Input 
                type="email" 
                placeholder="Email" 
                className="h-12 rounded-xl border-[#d2d2d7] bg-white"
              />
            </div>
            <div>
              <Input 
                type="password" 
                placeholder="Password" 
                className="h-12 rounded-xl border-[#d2d2d7] bg-white"
              />
            </div>
            <Button 
              className="w-full bg-[#0071e3] hover:bg-[#0077ed] text-white rounded-xl h-12 font-normal" 
              disabled
            >
              Sign in (Demo mode only)
            </Button>
          </div>
        </div>

        <p className="text-center text-sm text-[#86868b] mt-8">
          This is a demo environment. Click on any card above for instant access.
        </p>
        <p className="text-center text-sm text-[#86868b] mt-3">
          Career Counsellor?{' '}
          <a href="/counsellor-login" className="text-emerald-600 font-medium hover:underline" data-testid="counsellor-login-link">
            Sign in here
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
